import React from 'react';
import { resolveAssetUrl } from '../utils/resolveUrl';

/**
 * ProductSticker component renders either a text badge or an image sticker
 * positioned on a product card or product view based on corner anchor and pixel offsets.
 */
export default function ProductSticker({ sticker, isPreview = false, containerStyle = {} }) {
  if (!sticker) return null;

  const {
    type = 'text',
    text = 'OFERTË',
    bgColor = '#e11d48',
    textColor = '#ffffff',
    fontSize = 11,
    fontWeight = 700,
    borderRadius = 6,
    paddingX = 8,
    paddingY = 4,
    shadow = true,
    borderColor = '',
    borderWidth = 0,
    imageUrl = '',
    imageWidth = 65,
    imageHeight = 'auto',
    position = 'top-left',
    offsetX = 10,
    offsetY = 10,
    rotation = 0,
    zIndex = 12
  } = sticker;

  // Compute position coordinates from base corner and pixel offsets
  const posStyle = {};
  if (position === 'top-right') {
    posStyle.top = `${Number(offsetY) || 0}px`;
    posStyle.right = `${Number(offsetX) || 0}px`;
  } else if (position === 'bottom-left') {
    posStyle.bottom = `${Number(offsetY) || 0}px`;
    posStyle.left = `${Number(offsetX) || 0}px`;
  } else if (position === 'bottom-right') {
    posStyle.bottom = `${Number(offsetY) || 0}px`;
    posStyle.right = `${Number(offsetX) || 0}px`;
  } else {
    // Default 'top-left'
    posStyle.top = `${Number(offsetY) || 0}px`;
    posStyle.left = `${Number(offsetX) || 0}px`;
  }

  const wrapperStyle = {
    position: 'absolute',
    ...posStyle,
    zIndex: Number(zIndex) || 12,
    pointerEvents: isPreview ? 'auto' : 'none',
    userSelect: 'none',
    transform: Number(rotation) ? `rotate(${rotation}deg)` : undefined,
    transformOrigin: 'center center',
    transition: 'all 0.15s ease',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 'calc(100% - 10px)',
    ...containerStyle
  };

  if (type === 'image' && imageUrl) {
    const resolvedSrc = resolveAssetUrl(imageUrl);
    return (
      <div className="product-card-sticker product-card-sticker-image" style={wrapperStyle} title={sticker.name || ''}>
        <img
          src={resolvedSrc}
          alt={sticker.name || 'Sticker'}
          style={{
            width: `${Number(imageWidth) || 65}px`,
            height: imageHeight && imageHeight !== 'auto' ? `${Number(imageHeight)}px` : 'auto',
            objectFit: 'contain',
            display: 'block',
            filter: shadow ? 'drop-shadow(0 3px 6px rgba(0,0,0,0.22))' : 'none'
          }}
          loading="lazy"
          decoding="async"
        />
      </div>
    );
  }

  // Text Badge
  const badgeStyle = {
    backgroundColor: bgColor,
    color: textColor,
    fontSize: `${Number(fontSize) || 11}px`,
    fontWeight: Number(fontWeight) || 700,
    borderRadius: `${Number(borderRadius) || 6}px`,
    padding: `${Number(paddingY) || 4}px ${Number(paddingX) || 8}px`,
    border: borderWidth > 0 && borderColor ? `${borderWidth}px solid ${borderColor}` : 'none',
    boxShadow: shadow ? '0 3px 8px rgba(0,0,0,0.18)' : 'none',
    lineHeight: 1.25,
    whiteSpace: 'nowrap',
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    display: 'inline-block'
  };

  return (
    <div className="product-card-sticker product-card-sticker-text" style={wrapperStyle} title={sticker.name || ''}>
      <span style={badgeStyle}>
        {text}
      </span>
    </div>
  );
}
